import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Download, Zap, Info, Moon, Sun, X, RefreshCw, FileText, Merge, GripVertical } from "lucide-react";
import { PDFDocument } from "pdf-lib";
import { saveAs } from "file-saver";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ToolsNav from "../components/ToolsNav";
import { RenderCMSContent } from "../components/CMSRenderer";
import { AdContainer, ResponsiveAd, StickyMobileAd, DesktopOnlyAd } from "../components/AdPlacement";

function SortablePdfItem({ pdf, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: pdf.id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className="flex items-center gap-3 p-3 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg"
    >
      <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 hover:bg-slate-100 dark:hover:bg-slate-600 rounded">
        <GripVertical className="w-5 h-5 text-slate-400" />
      </button>
      <FileText className="w-8 h-8 text-red-500 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate dark:text-white">{pdf.name}</p>
        <p className="text-xs text-slate-500">{pdf.pages} pages • {formatFileSize(pdf.size)}</p>
      </div>
      <Button variant="ghost" size="icon" onClick={() => onRemove(pdf.id)} className="flex-shrink-0">
        <X className="w-4 h-4" />
      </Button>
    </div>
  );
}

function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function PdfMerge({ embedded = false }) {
  const [pdfs, setPdfs] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [pageContent, setPageContent] = useState(null);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInputRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    const fetchPageContent = async () => {
      try {
        const response = await fetch(`${API_URL}/api/pages/slug/pdf-merge`);
        if (response.ok) {
          const data = await response.json();
          setPageContent(data);
          document.title = data.seo?.meta_title || "Merge PDF Files - KayTool";
        }
      } catch (error) {
        document.title = "Merge PDF Files - KayTool";
      }
    };
    
    if (!embedded) {
      fetchPageContent();
    }
  }, [embedded]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(file => 
      file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
    );
    if (files.length > 0) {
      addPdfs(files);
    } else {
      toast.error("Please drop PDF files only");
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      addPdfs(files);
    }
    e.target.value = '';
  };

  const addPdfs = async (files) => {
    const newPdfs = [];
    
    for (const file of files) {
      try {
        const arrayBuffer = await file.arrayBuffer();
        // Use ignoreEncryption to handle locked/password-protected PDFs
        const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
        const pageCount = pdfDoc.getPageCount();
        
        newPdfs.push({
          id: Date.now() + Math.random(),
          name: file.name,
          file: file,
          arrayBuffer: arrayBuffer,
          pages: pageCount,
          size: file.size
        });
      } catch (error) {
        console.error(`Error loading ${file.name}:`, error);
        toast.error(`Failed to load ${file.name}. The PDF may be corrupted or have restrictions.`);
      }
    }

    if (newPdfs.length > 0) {
      setPdfs(prev => [...prev, ...newPdfs]);
      toast.success(`Added ${newPdfs.length} PDF${newPdfs.length > 1 ? 's' : ''}`);
    }
  };

  const removePdf = (id) => {
    setPdfs(prev => prev.filter(p => p.id !== id));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setPdfs((items) => {
        const oldIndex = items.findIndex(i => i.id === active.id);
        const newIndex = items.findIndex(i => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const mergePdfs = async () => {
    if (pdfs.length < 2) {
      toast.error("Please add at least 2 PDF files to merge");
      return;
    }

    setIsProcessing(true);
    setProgress(0);

    try {
      const mergedPdf = await PDFDocument.create();
      
      for (let i = 0; i < pdfs.length; i++) {
        const pdf = pdfs[i];
        setProgress(Math.round(((i + 1) / pdfs.length) * 90));
        
        // Use ignoreEncryption to handle locked PDFs during merge
        const pdfDoc = await PDFDocument.load(pdf.arrayBuffer, { ignoreEncryption: true });
        const pages = await mergedPdf.copyPages(pdfDoc, pdfDoc.getPageIndices());
        pages.forEach(page => mergedPdf.addPage(page));
      }

      setProgress(95);
      const mergedPdfBytes = await mergedPdf.save();
      setProgress(100);

      const blob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
      saveAs(blob, `merged-${Date.now()}.pdf`);
      
      toast.success("PDFs merged successfully!");
    } catch (error) {
      console.error("Merge error:", error);
      toast.error("Failed to merge PDFs");
    }

    setIsProcessing(false);
    setProgress(0);
  };

  const clearAll = () => {
    setPdfs([]);
  };

  const totalPages = pdfs.reduce((sum, pdf) => sum + pdf.pages, 0);
  const totalSize = pdfs.reduce((sum, pdf) => sum + pdf.size, 0);

  if (embedded) {
    return (
      <div className="p-4">
        <div
          onClick={() => fileInputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
            isDragging ? 'border-red-500 bg-red-50' : 'border-slate-300 hover:border-red-400'
          }`}
        >
          <Upload className="w-12 h-12 mx-auto mb-4 text-slate-400" />
          <p className="text-lg font-medium">Drop PDF files here or click to upload</p>
          <p className="text-sm text-slate-500 mt-2">Merge multiple PDFs into one</p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,application/pdf"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors duration-300">
      <Toaster position="top-center" theme={darkMode ? "dark" : "light"} />
      
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-800/70 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                KayTool
              </h1>
            </a>
            <div className="flex items-center gap-3">
              <ToolsNav />
              <Badge variant="secondary" className="hidden md:flex items-center gap-1 px-3 py-1 dark:bg-slate-700">
                <Info className="w-3 h-3" />
                100% Local Processing
              </Badge>
              <Button variant="outline" size="icon" onClick={() => setDarkMode(prev => !prev)} className="rounded-full">
                {darkMode ? <Sun className="w-5 h-5 text-yellow-500" /> : <Moon className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Ad Placement - Above the Fold (Below Header) */}
      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="container mx-auto px-4 py-8">
        {/* Hero */}
        <div className="text-center mb-8">
          <Badge className="mb-4 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
            PDF Merge
          </Badge>
          <h2 className="text-4xl font-bold text-slate-900 dark:text-white mb-4">
            Merge PDF Files
          </h2>
          <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Combine multiple PDF files into a single document. Drag and drop to reorder pages. Fast, free, and secure.
          </p>
        </div>

        {/* Upload Area */}
        <Card className="mb-6 dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-6">
            <div
              onClick={() => !isProcessing && fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all ${
                isProcessing ? 'opacity-50 cursor-not-allowed' :
                isDragging 
                  ? 'border-red-500 bg-red-50 dark:bg-red-900/20' 
                  : 'border-slate-300 dark:border-slate-600 hover:border-red-400 dark:hover:border-red-500'
              }`}
            >
              <Merge className="w-16 h-16 mx-auto mb-4 text-slate-400" />
              <p className="text-xl font-medium text-slate-700 dark:text-slate-300">
                Drop PDF files here or click to upload
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                Add multiple PDFs to merge them into one document
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              disabled={isProcessing}
            />
          </CardContent>
        </Card>

        {/* Progress */}
        {isProcessing && (
          <Card className="mb-6 dark:bg-slate-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Merging PDFs...</span>
                <span className="text-sm text-slate-500">{progress}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </CardContent>
          </Card>
        )}

        {/* PDF List */}
        {pdfs.length > 0 && (
          <>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  PDF Files ({pdfs.length})
                </h3>
                <p className="text-sm text-slate-500">
                  Total: {totalPages} pages • {formatFileSize(totalSize)}
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={clearAll} className="gap-2">
                  <RefreshCw className="w-4 h-4" /> Clear All
                </Button>
                <Button 
                  onClick={mergePdfs} 
                  disabled={pdfs.length < 2 || isProcessing}
                  className="gap-2 bg-red-600 hover:bg-red-700"
                >
                  <Merge className="w-4 h-4" /> Merge PDFs
                </Button>
              </div>
            </div>

            <Card className="dark:bg-slate-800 border-slate-200 dark:border-slate-700">
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-3">
                  Drag and drop to reorder PDFs. Files will be merged in this order.
                </p>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={pdfs.map(p => p.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-2">
                      {pdfs.map((pdf, index) => (
                        <div key={pdf.id} className="flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-xs font-bold flex items-center justify-center flex-shrink-0">
                            {index + 1}
                          </span>
                          <div className="flex-1">
                            <SortablePdfItem pdf={pdf} onRemove={removePdf} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </CardContent>
            </Card>
          </>
        )}

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
          {[
            { title: "Drag & Drop Reorder", desc: "Easily arrange PDFs in your preferred order before merging" },
            { title: "No File Limits", desc: "Merge as many PDFs as you need - no restrictions" },
            { title: "100% Private", desc: "All processing happens in your browser - files never leave your device" }
          ].map((feature, i) => (
            <Card key={i} className="dark:bg-slate-800">
              <CardContent className="p-6 text-center">
                <h4 className="font-semibold mb-2 dark:text-white">{feature.title}</h4>
                <p className="text-sm text-slate-600 dark:text-slate-400">{feature.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Ad Placement - After Features */}
        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>
      </main>

      {/* CMS Content */}
      {pageContent?.content && (
        <div className="bg-white dark:bg-slate-800 py-12">
          <div className="container mx-auto px-4">
            <RenderCMSContent content={pageContent.content} />
          </div>
        </div>
      )}

      {/* Ad Placement - Before Footer */}
      <AdContainer className="my-6">
        <ResponsiveAd slot="before-footer" mobileSize="mobile-rectangle" desktopSize="rectangle" />
      </AdContainer>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600 dark:text-slate-400">
          <p>&copy; {new Date().getFullYear()} KayTool. All processing happens locally in your browser.</p>
        </div>
      </footer>

      {/* Sticky Mobile Ad */}
      <StickyMobileAd slot="sticky-footer" />

    </div>
  );
}

export default PdfMerge;
